import React, { useEffect, useState } from 'react'

import { useHistory } from 'react-router-dom'
import { useMyScopeActions } from 'redux/actions/MyScopes'
import { useMyScopes } from 'redux/hooks/MyScopes'
import {
  useAvailableUnits,
  useAvailableUnitsFetched
} from 'redux/hooks/AvailableUnits'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

import { useTranslation } from 'language/translator'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { Action } from 'types/GlobalUserEvents'

import Button from 'components_new/atoms/Button'

import SearchAndSelect from 'components_new/molecules/SearchAndSelect'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { getContextFromPath } from 'utils/contextPaths'

interface MyOrgUnitsDialogProps {
  onClose: () => void
  onSave: () => void
  open: boolean
}

const MyOrgUnitsDialog = React.forwardRef(
  (props: MyOrgUnitsDialogProps, ref) => {
    const { onClose, open, onSave } = props

    const history = useHistory()
    const { lang, t } = useTranslation()
    const { tryUpdateMyScopes } = useMyScopeActions()
    const { isCompanyBased } = useAccountSettings()

    // Get the account's saved scopes from Redux (loaded on login)
    const myStandard = useMyScopes()

    // Get available units from Redux (loaded on login)
    const availableUnits = useAvailableUnits()
    const availableFetched = useAvailableUnitsFetched()

    // Sort available units by language
    const sortedOrgUnits = [...availableUnits].sort((a, b) =>
      a.localeCompare(b, lang)
    )

    const [tempSelected, setTempSelected] = useState<string[]>([])

    useEffect(() => {
      setTempSelected(myStandard)
    }, [myStandard, open])

    const handleSave = async () => {
      await tryUpdateMyScopes(tempSelected)

      UserEvents.log({
        action: Action.MY_REAL_ESTATES_SAVED,
        context: getContextFromPath(history.location.pathname),
        dashboard_id: null,
        dashboard_group_id: null
      })

      onSave()
      handleClose()
    }

    const handleClose = () => {
      onClose()
    }

    /*-- default --*/
    return (
      <SimpleDialog
        actions={
          <>
            <Button onClick={handleClose} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button onClick={handleSave}>{t('global', 'save')}</Button>
          </>
        }
        hasClose={true}
        helperText={t(
          'org_units',
          isCompanyBased ? 'helper_text_company' : 'helper_text'
        )}
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        ref={ref}
        title={t('org_units', isCompanyBased ? 'standard_company' : 'standard')}
      >
        <SearchAndSelect
          autoFocus={true}
          loading={!availableFetched}
          options={sortedOrgUnits.map((orgUnit) => {
            return { id: orgUnit, title: orgUnit }
          })}
          onChange={(value) => setTempSelected(value)}
          value={tempSelected}
        />
      </SimpleDialog>
    )
  }
)

export default MyOrgUnitsDialog
